import type { ProfileSummary } from "@pax/api-contract/common";
import type { ModeratedSurface } from "@pax/api-contract/moderation/v1alpha1";
import { Link, type LinkProps } from "@tanstack/react-router";
import { formatCount } from "@/features/evals/components";
import { PersonFace } from "@/features/staff/components";

export const STATS_PERIODS = [7, 30, 90] as const;
export type StatsPeriod = (typeof STATS_PERIODS)[number];

/** A count against the period of the same length before it: "Up from 6 in the previous 30 days". */
export function Change({
  count,
  previous,
  days,
}: {
  count: number;
  previous: number;
  days: StatsPeriod;
}) {
  const before = `the previous ${days} days`;
  return (
    <span className="block">
      {count === previous
        ? `Same as ${before}`
        : `${count > previous ? "Up" : "Down"} from ${formatCount(previous)} in ${before}`}
    </span>
  );
}

/**
 * Counts as thin bars against the largest, or against `max`, each with its label and number.
 * A row with `to` opens what it counts.
 */
export function BarList({
  rows,
  max = Math.max(1, ...rows.map(({ count }) => count)),
}: {
  rows: readonly {
    key: string;
    label: string;
    count: number;
    note?: string;
    to?: LinkProps["search"];
  }[];
  max?: number;
}) {
  return (
    <ul className="space-y-2">
      {rows.map(row => (
        <li key={row.key} className="grid grid-cols-[10rem_1fr_4.5rem] items-center gap-3">
          <span className="truncate text-xs">
            {row.to ? (
              <Link to="/moderation" search={row.to} className="hover:underline">
                {row.label}
              </Link>
            ) : (
              row.label
            )}
          </span>
          <span className="h-1.5 overflow-hidden rounded-full bg-foreground/[0.06]">
            <span
              className="block h-full rounded-full bg-foreground/60"
              style={{ width: `${max === 0 ? 0 : (row.count / max) * 100}%` }}
            />
          </span>
          <span className="text-right text-xs text-muted-foreground tabular-nums">
            {formatCount(row.count)}
            {row.note && ` (${row.note})`}
          </span>
        </li>
      ))}
    </ul>
  );
}

export type OpenUser = (userId: string, surface: ModeratedSurface | undefined) => void;

/** Someone by picture and name, opening their record when the viewer can read it. */
export function PersonName({
  userId,
  person,
  surface,
  openUser,
}: {
  userId: string;
  person: ProfileSummary | undefined;
  /** The surface their record opens on; live games open on whichever the viewer moderates. */
  surface: ModeratedSurface | undefined;
  openUser?: OpenUser;
}) {
  const named = (
    <span className="flex min-w-0 items-center gap-2">
      {person && (
        <span aria-hidden className="shrink-0">
          <PersonFace person={person} size={24} />
        </span>
      )}
      <span className="truncate">{person?.displayName ?? "Deleted account"}</span>
    </span>
  );
  if (!openUser) return named;
  return (
    <button
      type="button"
      onClick={() => openUser(userId, surface)}
      className="min-w-0 text-left hover:underline"
    >
      {named}
    </button>
  );
}
